import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import s from "./DatePicker.module.css";

/**
 * Birthday-friendly date picker:
 *   – compact MM/DD/YYYY input
 *   – popover with a year dropdown (so you can jump 30 years in one click)
 *     + month nav arrows + a 7-col day grid
 *   – value/onChange use ISO "YYYY-MM-DD" strings (or "" when empty)
 *
 * Props: value, onChange(iso), min (ISO), max (ISO), className, placeholder
 */
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseISO(v) {
  if (!v || typeof v !== "string") return null;
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2] - 1, d: +m[3] };
}

function toISO(y, mo, d) {
  const mm = String(mo + 1).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

function daysInMonth(y, mo) {
  return new Date(y, mo + 1, 0).getDate();
}

function formatDisplay(iso) {
  const p = parseISO(iso);
  if (!p) return "";
  const mm = String(p.mo + 1).padStart(2, "0");
  const dd = String(p.d).padStart(2, "0");
  return `${mm}/${dd}/${p.y}`;
}

export default function DatePicker({ value, onChange, min, max, className, placeholder = "MM/DD/YYYY" }) {
  const parsed = parseISO(value);
  const today = new Date();

  // View state — which month/year the calendar grid is showing
  const [viewY, setViewY] = useState(parsed?.y ?? today.getFullYear());
  const [viewM, setViewM] = useState(parsed?.mo ?? today.getMonth());
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(formatDisplay(value));
  const wrapRef    = useRef(null);
  const inputRowRef = useRef(null);
  const popupRef   = useRef(null);
  // Popover renders via a portal to document.body and uses position:fixed
  // with JS-computed coords. Two reasons: (1) escapes any parent's
  // overflow:hidden / auto so it can't be clipped, (2) escapes any parent
  // CSS transform (modals often use `translate(-50%,-50%)` for centering)
  // that would otherwise warp getBoundingClientRect-based positioning.
  // Anchored to the input ROW rect so the popup sits flush under the
  // visible input regardless of label or wrap padding.
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const POPUP_WIDTH = 320;          // matches .popover width below
  const POPUP_HEIGHT_ESTIMATE = 380; // used for the bottom-edge guard

  function recomputeCoords() {
    const el = inputRowRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Right-edge guard — shift the popup leftward if it would overflow
    // the viewport. 8px gutter so it never hugs the edge.
    const overshootRight = (rect.left + POPUP_WIDTH) - (window.innerWidth - 8);
    const left = Math.max(8, rect.left - Math.max(0, overshootRight));
    // Bottom-edge guard — flip above the input when there isn't room
    // below. Avoids the calendar getting clipped on short viewports
    // (modals near the screen bottom).
    const wouldOverflowBottom = rect.bottom + 8 + POPUP_HEIGHT_ESTIMATE > window.innerHeight;
    const top = wouldOverflowBottom
      ? Math.max(8, rect.top - 8 - POPUP_HEIGHT_ESTIMATE)
      : rect.bottom + 8;
    setCoords({ top, left });
  }

  // Re-sync when the external value changes
  useEffect(() => {
    setText(formatDisplay(value));
    if (parsed) { setViewY(parsed.y); setViewM(parsed.mo); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close on outside click. The popup now renders with position:fixed,
  // so it lives outside wrapRef's DOM tree — check popupRef too.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current?.contains(e.target))  return;
      if (popupRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Compute coords synchronously before paint so the popup never flashes
  // in the wrong spot. useLayoutEffect runs after DOM mutation but before
  // the browser paints, so the very first frame the popup is visible it
  // already has the correct top/left.
  useLayoutEffect(() => {
    if (!open) return;
    recomputeCoords();
  }, [open]);

  // Keep the popup anchored while the page / modal scrolls or resizes.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", recomputeCoords);
    window.addEventListener("scroll",  recomputeCoords, true);
    return () => {
      window.removeEventListener("resize", recomputeCoords);
      window.removeEventListener("scroll",  recomputeCoords, true);
    };
  }, [open]);

  const minP = parseISO(min);
  const maxP = parseISO(max);

  function isDisabled(y, mo, d) {
    const t = new Date(y, mo, d).getTime();
    if (minP && t < new Date(minP.y, minP.mo, minP.d).getTime()) return true;
    if (maxP && t > new Date(maxP.y, maxP.mo, maxP.d).getTime()) return true;
    return false;
  }

  function pickDay(d) {
    if (isDisabled(viewY, viewM, d)) return;
    const iso = toISO(viewY, viewM, d);
    onChange(iso);
    setText(formatDisplay(iso));
    setOpen(false);
  }

  function prevMonth() {
    if (viewM === 0) { setViewM(11); setViewY(y => y - 1); }
    else setViewM(m => m - 1);
  }
  function nextMonth() {
    if (viewM === 11) { setViewM(0); setViewY(y => y + 1); }
    else setViewM(m => m + 1);
  }

  function onTextChange(e) {
    const raw = e.target.value;
    setText(raw);
    const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const mo = +m[1] - 1, d = +m[2], y = +m[3];
      if (mo >= 0 && mo < 12 && d >= 1 && d <= daysInMonth(y, mo)) {
        const iso = toISO(y, mo, d);
        onChange(iso);
        setViewY(y); setViewM(mo);
      }
    } else if (raw === "") {
      onChange("");
    }
  }

  function clearAll() {
    onChange("");
    setText("");
    setOpen(false);
  }

  // Build the 7-col grid (leading blanks for day-of-week offset of the 1st)
  const firstDow = new Date(viewY, viewM, 1).getDay();
  const totalDays = daysInMonth(viewY, viewM);
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  // Year range — 110 years back, 5 forward (covers birthdays)
  const currentYear = today.getFullYear();
  const years = [];
  for (let y = currentYear + 5; y >= currentYear - 110; y--) years.push(y);

  return (
    <div className={`${s.wrap} ${className || ""}`} ref={wrapRef}>
      <div className={`${s.inputRow} ${open ? s.inputRowOpen : ""}`} ref={inputRowRef}>
        <input
          className={s.input}
          value={text}
          onChange={onTextChange}
          onFocus={() => { recomputeCoords(); setOpen(true); }}
          placeholder={placeholder}
        />
        {text && (
          <button type="button" className={s.clearBtn} onClick={clearAll} title="Clear">×</button>
        )}
        <button type="button" className={s.calBtn}
                onClick={() => { if (!open) recomputeCoords(); setOpen(o => !o); }}
                aria-label="Open calendar">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="3" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </button>
      </div>

      {open && createPortal(
        <div className={s.popover} role="dialog"
             ref={popupRef}
             style={{ top: coords.top, left: coords.left }}>
          <div className={s.head}>
            <button type="button" className={s.navBtn} onClick={prevMonth} aria-label="Previous month">‹</button>
            <div className={s.headPickers}>
              <select
                className={s.monthSelect}
                value={viewM}
                onChange={e => setViewM(+e.target.value)}>
                {MONTHS.map((name, i) => <option key={i} value={i}>{name}</option>)}
              </select>
              <select
                className={s.yearSelect}
                value={viewY}
                onChange={e => setViewY(+e.target.value)}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button type="button" className={s.navBtn} onClick={nextMonth} aria-label="Next month">›</button>
          </div>

          <div className={s.dowRow}>
            {DAYS.map(d => <span key={d} className={s.dow}>{d}</span>)}
          </div>

          <div className={s.grid}>
            {cells.map((d, i) => {
              if (d === null) return <span key={i} className={s.cellBlank} />;
              const iso = toISO(viewY, viewM, d);
              const isSelected = iso === value;
              const isToday = d === today.getDate() && viewM === today.getMonth() && viewY === today.getFullYear();
              const disabled = isDisabled(viewY, viewM, d);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  className={`${s.cell} ${isSelected ? s.cellSelected : ""} ${isToday && !isSelected ? s.cellToday : ""}`}
                  onClick={() => pickDay(d)}>
                  {d}
                </button>
              );
            })}
          </div>

          <div className={s.foot}>
            <button type="button" className={s.footBtn} onClick={() => {
              const iso = toISO(today.getFullYear(), today.getMonth(), today.getDate());
              if (isDisabled(today.getFullYear(), today.getMonth(), today.getDate())) return;
              onChange(iso);
              setText(formatDisplay(iso));
              setViewY(today.getFullYear()); setViewM(today.getMonth());
              setOpen(false);
            }}>Today</button>
            <button type="button" className={s.footBtn} onClick={clearAll}>Clear</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
